import { useState, useEffect, useCallback, useRef } from 'react';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SearchableSelect from '../../components/common/SearchableSelect';
import ImageCropper from '../../components/common/ImageCropper';
import { usePaginacao } from '../../hooks/usePaginacao';
import { useSortable } from '../../hooks/useSortable';
import { useAuth } from '../../contexts/AuthContext';
import { formatMoeda } from '../../utils/fmt';
import * as api from '../../api/modelos';
import * as tiposApi from '../../api/tipos';
import * as coresApi from '../../api/cores';
import * as clientesApi from '../../api/clientes';
import * as tamanhosApi from '../../api/tamanhos';
import * as empresasApi from '../../api/empresas';

const NOME_TAMANHO_UNICO = 'ÚNICO';

/* ── Criação rápida de cor (sem sair do cadastro de modelo) ──── */
function ModalNovaCor({ onClose, onCriada }) {
  const [nome, setNome] = useState('');
  const [hex, setHex] = useState('#000000');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const salvar = async (e) => {
    e.preventDefault();
    setErro('');
    setLoading(true);
    try {
      const { data } = await coresApi.criar({ nome, codigo_hex: hex });
      onCriada(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setErro(typeof d === 'string' ? d : 'Erro ao salvar.');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.55)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered modal-sm">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">Nova Cor</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={salvar}>
            <div className="modal-body">
              {erro && <div className="alert alert-danger py-2">{erro}</div>}
              <div className="mb-3">
                <label className="form-label fw-semibold">Nome *</label>
                <input className="form-control" value={nome} onChange={e => setNome(e.target.value)} required autoFocus />
              </div>
              <div>
                <label className="form-label fw-semibold">Cor</label>
                <input type="color" className="form-control form-control-color" value={hex} onChange={e => setHex(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm" /> : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

/* ── Criação rápida de tamanho (sem sair do cadastro de modelo) ──── */
function ModalNovoTamanho({ empresaAlvo, emGlobal, onClose, onCriado }) {
  const [nome, setNome] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const salvar = async (e) => {
    e.preventDefault();
    setErro('');
    setLoading(true);
    try {
      const payload = { nome };
      if (emGlobal && empresaAlvo) payload.id_empresa_operadora = empresaAlvo;
      const { data } = await tamanhosApi.criar(payload);
      onCriado(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setErro(typeof d === 'string' ? d : 'Erro ao salvar.');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.55)', zIndex: 1060 }}>
      <div className="modal-dialog modal-dialog-centered modal-sm">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">Novo Tamanho</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={salvar}>
            <div className="modal-body">
              {erro && <div className="alert alert-danger py-2">{erro}</div>}
              <label className="form-label fw-semibold">Nome *</label>
              <input className="form-control" value={nome} onChange={e => setNome(e.target.value)}
                placeholder="Ex: P, M, G, 42..." required autoFocus />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm" /> : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function ModalFotos({ modelo, onClose, onSaved }) {
  const [fotos, setFotos] = useState(modelo.fotos || []);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);   // object URL da imagem selecionada
  const fileRef = useRef();

  /* Abre o cropper ao selecionar arquivo */
  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setCropSrc(url);
    fileRef.current.value = '';   // reset para permitir reselecionar o mesmo arquivo
  };

  /* Usuário cancelou o crop */
  const onCropCancel = () => {
    URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  /* Usuário confirmou o recorte — recebe o blob 800×600 JPEG */
  const onCropConfirm = async (blob) => {
    URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, 'foto.jpg');
      const r = await api.uploadFoto(modelo.id_modelo_uniforme, fd);
      setFotos(f => [...f, r.data]);
      onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro no upload.');
    } finally { setUploading(false); }
  };

  const remover = async (idFoto) => {
    if (!window.confirm('Remover esta foto?')) return;
    await api.deletarFoto(modelo.id_modelo_uniforme, idFoto);
    setFotos(f => f.filter(x => x.id_foto !== idFoto));
    onSaved();
  };

  return (
    <>
      <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title mb-0">Fotos — {modelo.nome}</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">

              {/* Grade de thumbnails 4:3 */}
              <div className="d-flex flex-wrap gap-3 mb-3">
                {fotos.length === 0 && <p className="text-muted">Nenhuma foto ainda.</p>}
                {fotos.map(f => (
                  <div key={f.id_foto} style={{ position: 'relative', width: 160 }}>
                    {/* Container com proporção 4:3 */}
                    <div style={{ position: 'relative', paddingTop: '75%', borderRadius: 4, overflow: 'hidden', border: '1px solid #ddd' }}>
                      <img src={f.url} alt={`foto ${f.ordem}`}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                      <span style={{ position: 'absolute', top: 4, left: 6, background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 11, padding: '1px 6px', borderRadius: 3 }}>
                        #{f.ordem}
                      </span>
                    </div>
                    <button
                      className="btn btn-outline-danger btn-sm w-100 mt-1"
                      style={{ fontSize: 11, padding: '2px 0' }}
                      onClick={() => remover(f.id_foto)}>
                      Remover
                    </button>
                  </div>
                ))}
              </div>

              {fotos.length < 5 && (
                <div>
                  <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                    Adicionar foto
                  </label>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                    className="form-control" onChange={onFileChange} disabled={uploading} />
                  {uploading && (
                    <div className="d-flex align-items-center gap-2 text-muted mt-2" style={{ fontSize: 13 }}>
                      <div className="spinner-border spinner-border-sm" /> Enviando...
                    </div>
                  )}
                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                    JPEG, PNG ou WebP &nbsp;·&nbsp; Proporção 4:3 (800×600px após recorte)
                    &nbsp;·&nbsp; {5 - fotos.length} vaga{5 - fotos.length !== 1 ? 's' : ''} restante{5 - fotos.length !== 1 ? 's' : ''}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>Fechar</button>
            </div>
          </div>
        </div>
      </div>

      {/* Cropper — sobrepõe o modal de fotos */}
      {cropSrc && (
        <ImageCropper
          src={cropSrc}
          onConfirm={onCropConfirm}
          onCancel={onCropCancel}
        />
      )}
    </>
  );
}

function ModalModelo({ item, tipos, cores, tamanhos, clientes, empresas, emGlobal, onClose, onSaved, onCorCriada, onTamanhoCriado }) {
  const editando = !!item?.id_modelo_uniforme;
  const [form, setForm] = useState({
    nome: item?.nome || '',
    descricao: item?.descricao || '',
    preco_unitario: item?.preco_unitario || '',
    id_tipo_uniforme: item?.id_tipo_uniforme || '',
    id_cliente_final: item?.id_cliente_final || '',
    prazo_fabricacao_dias: item?.prazo_fabricacao_dias || '',
    // Editável só por admin em Modo Global (ver handleEmpresaChange) — trocar a
    // empresa de um modelo já existente reseta tipo/tamanhos/cores, que são
    // cadastros por empresa e não fazem sentido na empresa de destino.
    id_empresa_operadora: item?.id_empresa_operadora || '',
  });
  const [tipoPreco, setTipoPreco] = useState(item?.tipo_preco || 'unico');
  const [tamanhosSel, setTamanhosSel] = useState(item?.tamanhos?.map(t => t.id_tamanho_uniforme) || []);
  const [tamanhoUnico, setTamanhoUnico] = useState(
    item?.tamanhos?.length === 1 &&
    tamanhos.find(t => t.id_tamanho_uniforme === item.tamanhos[0].id_tamanho_uniforme)?.nome === NOME_TAMANHO_UNICO
  );
  const [precosPorTamanho, setPrecosPorTamanho] = useState(
    Object.fromEntries((item?.tamanhos || []).map(t => [t.id_tamanho_uniforme, t.preco_unitario ?? '']))
  );
  const [medidasPorTamanho, setMedidasPorTamanho] = useState(
    Object.fromEntries((item?.tamanhos || []).map(t => [t.id_tamanho_uniforme, t.medida_referencia ?? '']))
  );
  const [permiteCor, setPermiteCor] = useState(item?.permite_variacao_cor || false);
  const [coresSel, setCoresSel] = useState(item?.cores?.map(c => c.id_cor) || []);
  const [modalNovaCor, setModalNovaCor] = useState(false);
  const [modalNovoTamanho, setModalNovoTamanho] = useState(false);
  const [criandoUnico, setCriandoUnico] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  // Empresa "dona" do modelo neste form: em Modo Global (admin) é a que foi
  // escolhida aqui (editável mesmo já existindo o modelo); nos outros casos
  // (empresa ativa já definida) nem aparece campo — o backend resolve pela sessão.
  const empresaAlvo = form.id_empresa_operadora;

  // No Modo Global os tamanhos vêm de todas as empresas misturados — filtra pela
  // empresa alvo deste modelo, senão a grade mistura tamanhos de outras empresas.
  const tamanhosDaEmpresa = emGlobal ? tamanhos.filter(t => t.id_empresa_operadora === empresaAlvo) : tamanhos;
  // Mesma lógica — cor também é por empresa agora.
  const coresDaEmpresa = emGlobal ? cores.filter(c => c.id_empresa_operadora === empresaAlvo) : cores;

  const set = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleEmpresaChange = (v) => {
    setForm(f => ({ ...f, id_empresa_operadora: v, id_tipo_uniforme: '', id_cliente_final: '' }));
    // Tipo/tamanhos/cores são cadastros por empresa — a seleção anterior (inclusive a
    // que já veio do modelo, se estiver editando) não existe na empresa de destino.
    setTamanhosSel([]);
    setTamanhoUnico(false);
    setPrecosPorTamanho({});
    setMedidasPorTamanho({});
    setCoresSel([]);
  };

  const toggleTam = (t) => setTamanhosSel(s =>
    s.includes(t) ? s.filter(x => x !== t) : [...s, t]
  );
  const toggleTamanhoUnico = async (checked) => {
    setTamanhoUnico(checked);
    if (!checked) { setTamanhosSel([]); return; }
    let unico = tamanhosDaEmpresa.find(t => t.nome.toUpperCase() === NOME_TAMANHO_UNICO);
    if (!unico) {
      setCriandoUnico(true);
      try {
        const payload = { nome: NOME_TAMANHO_UNICO };
        if (emGlobal && empresaAlvo) payload.id_empresa_operadora = empresaAlvo;
        const { data } = await tamanhosApi.criar(payload);
        onTamanhoCriado(data);
        unico = data;
      } catch (err) {
        setErro(err.response?.data?.detail || 'Erro ao criar o tamanho "ÚNICO".');
        setTamanhoUnico(false);
        setCriandoUnico(false);
        return;
      }
      setCriandoUnico(false);
    }
    setTamanhosSel([unico.id_tamanho_uniforme]);
  };
  const toggleCor = (id) => setCoresSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const corRecemCriada = (novaCor) => {
    onCorCriada(novaCor);
    setCoresSel(s => [...s, novaCor.id_cor]);
    setModalNovaCor(false);
  };

  const tamanhoRecemCriado = (novoTamanho) => {
    onTamanhoCriado(novoTamanho);
    setTamanhosSel(s => [...s, novoTamanho.id_tamanho_uniforme]);
    setModalNovoTamanho(false);
  };

  const tipoSelecionado = tipos.find(t => t.id_tipo_uniforme === form.id_tipo_uniforme);
  const copiarGradeDoTipo = () => {
    if (!tipoSelecionado) return;
    setTamanhosSel((tipoSelecionado.tamanhos_padrao || []).map(t => t.id_tamanho_uniforme));
    setMedidasPorTamanho(Object.fromEntries((tipoSelecionado.tamanhos_padrao || []).map(t => [t.id_tamanho_uniforme, t.medida_referencia || ''])));
    if (permiteCor) setCoresSel((tipoSelecionado.cores_padrao || []).map(c => c.id_cor));
  };

  const salvar = async (e) => {
    e.preventDefault();
    if (emGlobal && !form.id_empresa_operadora) {
      setErro('Selecione a empresa dona deste modelo.');
      return;
    }
    if (editando && emGlobal && form.id_empresa_operadora !== item.id_empresa_operadora) {
      if (!window.confirm('Mudar a empresa deste modelo? Tipo, tamanhos e cores serão resetados e precisam ser escolhidos de novo, dentro da empresa de destino.')) {
        return;
      }
    }
    if (tamanhosSel.length === 0) { setErro('Selecione ao menos um tamanho.'); return; }
    if (tipoPreco === 'por_tamanho' && tamanhosSel.some(t => !precosPorTamanho[t])) {
      setErro('Informe o preço de todos os tamanhos selecionados.');
      return;
    }
    setErro('');
    setLoading(true);
    try {
      const payload = {
        nome: form.nome,
        descricao: form.descricao,
        id_tipo_uniforme: form.id_tipo_uniforme,
        id_cliente_final: form.id_cliente_final || null,
        prazo_fabricacao_dias: form.prazo_fabricacao_dias ? parseInt(form.prazo_fabricacao_dias, 10) : null,
        tipo_preco: tipoPreco,
        preco_unitario: tipoPreco === 'unico' ? parseFloat(form.preco_unitario) : null,
        permite_variacao_cor: permiteCor,
        cores: permiteCor ? coresSel : [],
        tamanhos: tamanhosSel.map(t => ({
          id_tamanho_uniforme: t,
          preco_unitario: tipoPreco === 'por_tamanho' ? parseFloat(precosPorTamanho[t]) : null,
          medida_referencia: medidasPorTamanho[t] || null,
        })),
      };
      if (emGlobal) payload.id_empresa_operadora = form.id_empresa_operadora;
      if (editando) await api.atualizar(item.id_modelo_uniforme, payload);
      else await api.criar(payload);
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      setErro(typeof d === 'string' ? d : 'Erro ao salvar.');
    } finally { setLoading(false); }
  };

  const toggleAtivo = async () => {
    setLoading(true);
    try {
      await api.atualizar(item.id_modelo_uniforme, { ativo: !item.ativo });
      onSaved();
    } finally { setLoading(false); }
  };

  // No Modo Global os tipos vêm de todas as empresas misturados — precisa filtrar
  // pela empresa alvo deste modelo, senão o dropdown mistura tipos de outras empresas.
  const tiposDaEmpresa = emGlobal ? tipos.filter(t => t.id_empresa_operadora === empresaAlvo) : tipos;
  const tipoOpts = tiposDaEmpresa.map(t => ({ value: t.id_tipo_uniforme, label: t.nome }));
  const empresaOpts = empresas.map(e => ({ value: e.id_empresa_operadora, label: e.nome }));
  // Mesmo problema de tipos/cores/tamanhos no Modo Global: `clientes` vem sem filtro de
  // empresa (cliente_final não tem 1 empresa fixa) — filtra pela empresa alvo deste
  // modelo usando o `empresas: [...]` que cada cliente já traz do backend.
  const clientesDaEmpresa = emGlobal
    ? clientes.filter(c => (c.empresas || []).some(e => e.id_empresa_operadora === empresaAlvo && e.ativo))
    : clientes;
  const clienteOpts = [{ value: '', label: 'Geral da empresa (todos os clientes)' },
    ...clientesDaEmpresa.map(c => ({ value: c.id_cliente_final, label: c.nome }))];

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">{editando ? 'Editar Modelo' : 'Novo Modelo'}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={salvar}>
            <div className="modal-body">
              {erro && <div className="alert alert-danger py-2">{erro}</div>}
              <div className="row g-3">
                {emGlobal && (
                  <div className="col-12">
                    <label className="form-label fw-semibold">Empresa *</label>
                    <SearchableSelect options={empresaOpts} value={form.id_empresa_operadora}
                      onChange={handleEmpresaChange}
                      placeholder="Selecione a empresa dona deste modelo..." required />
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                      {editando
                        ? 'Você está no modo global — mudar a empresa reseta tipo, tamanhos e cores (são cadastros por empresa). Só é possível se este modelo ainda não tiver estoque, pedidos ou demandas registradas.'
                        : 'Você está no modo global — escolha para qual empresa este modelo será cadastrado.'}
                    </div>
                  </div>
                )}
                <div className="col-md-8">
                  <label className="form-label fw-semibold">Nome *</label>
                  <input name="nome" className="form-control" value={form.nome} onChange={set} required />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Prazo de fabricação (dias)</label>
                  <input name="prazo_fabricacao_dias" type="number" min="0" className="form-control"
                    value={form.prazo_fabricacao_dias} onChange={set} placeholder="Opcional" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Cliente</label>
                  <SearchableSelect options={clienteOpts} value={form.id_cliente_final}
                    onChange={v => setForm(f => ({ ...f, id_cliente_final: v }))}
                    placeholder="Geral da empresa (todos os clientes)" />
                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                    Deixe em branco para um catálogo geral, visível a todos os clientes da empresa.
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Tipo de Uniforme *</label>
                  <div className="d-flex gap-2">
                    <div className="flex-grow-1">
                      <SearchableSelect options={tipoOpts} value={form.id_tipo_uniforme}
                        disabled={emGlobal && !empresaAlvo}
                        onChange={v => setForm(f => ({ ...f, id_tipo_uniforme: v }))}
                        placeholder="Selecione o tipo..." required />
                    </div>
                    <button type="button" className="btn btn-outline-secondary btn-sm" disabled={!tipoSelecionado || tamanhoUnico}
                      onClick={copiarGradeDoTipo} title="Copiar grade padrão do tipo">
                      Copiar grade
                    </button>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label fw-semibold">Descrição</label>
                  <textarea name="descricao" className="form-control" rows={2}
                    value={form.descricao} onChange={set} />
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold d-block">Preço</label>
                  <div className="btn-group btn-group-sm mb-2">
                    <button type="button" className={`btn ${tipoPreco === 'unico' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setTipoPreco('unico')}>Preço único</button>
                    <button type="button" className={`btn ${tipoPreco === 'por_tamanho' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setTipoPreco('por_tamanho')}>Preço por tamanho</button>
                  </div>
                  {tipoPreco === 'unico' && (
                    <div className="input-group" style={{ maxWidth: 220 }}>
                      <span className="input-group-text">R$</span>
                      <input name="preco_unitario" type="number" step="0.01" min="0"
                        className="form-control" value={form.preco_unitario} onChange={set} required />
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold d-block">Tamanhos *</label>
                  <div className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" id="tamanhoUnico"
                      checked={tamanhoUnico} disabled={criandoUnico || (emGlobal && !empresaAlvo)}
                      onChange={e => toggleTamanhoUnico(e.target.checked)} />
                    <label className="form-check-label" htmlFor="tamanhoUnico">
                      Produto é tamanho único (sem grade de tamanhos)
                      {criandoUnico && <span className="spinner-border spinner-border-sm ms-2" />}
                    </label>
                  </div>
                  {!tamanhoUnico && (
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {tamanhosDaEmpresa.filter(t => t.nome.toUpperCase() !== NOME_TAMANHO_UNICO).map(t => (
                        <button key={t.id_tamanho_uniforme} type="button"
                          className={`btn btn-sm ${tamanhosSel.includes(t.id_tamanho_uniforme) ? 'btn-primary' : 'btn-outline-secondary'}`}
                          style={{ minWidth: 44 }}
                          onClick={() => toggleTam(t.id_tamanho_uniforme)}>
                          {t.nome}
                        </button>
                      ))}
                      <button type="button" className="btn btn-sm btn-outline-primary"
                        disabled={emGlobal && !empresaAlvo}
                        onClick={() => setModalNovoTamanho(true)} title="Cadastrar novo tamanho">
                        + Novo tamanho
                      </button>
                    </div>
                  )}
                  {tamanhosSel.length > 0 && (
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Tamanho</th>
                          {tipoPreco === 'por_tamanho' && <th>Preço (R$) *</th>}
                          <th>Medida de referência</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tamanhosSel.map(t => (
                          <tr key={t}>
                            <td className="fw-semibold">{tamanhos.find(x => x.id_tamanho_uniforme === t)?.nome || t}</td>
                            {tipoPreco === 'por_tamanho' && (
                              <td>
                                <input type="number" step="0.01" min="0" className="form-control form-control-sm"
                                  value={precosPorTamanho[t] || ''}
                                  onChange={e => setPrecosPorTamanho(p => ({ ...p, [t]: e.target.value }))} required />
                              </td>
                            )}
                            <td>
                              <input type="text" className="form-control form-control-sm"
                                placeholder="Ex: Busto 96cm"
                                value={medidasPorTamanho[t] || ''}
                                onChange={e => setMedidasPorTamanho(m => ({ ...m, [t]: e.target.value }))} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="col-12">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="permiteCor"
                      checked={permiteCor} onChange={e => setPermiteCor(e.target.checked)} />
                    <label className="form-check-label fw-semibold" htmlFor="permiteCor">
                      Modelo tem variação de cor
                    </label>
                  </div>
                  {permiteCor && (
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                      {coresDaEmpresa.map(c => (
                        <button key={c.id_cor} type="button"
                          className={`btn btn-sm ${coresSel.includes(c.id_cor) ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => toggleCor(c.id_cor)}>
                          {c.codigo_hex && (
                            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: c.codigo_hex, marginRight: 6 }} />
                          )}
                          {c.nome}
                        </button>
                      ))}
                      <button type="button" className="btn btn-sm btn-outline-primary"
                        onClick={() => setModalNovaCor(true)} title="Cadastrar nova cor">
                        + Nova cor
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-between">
              {editando ? (
                <button type="button" className={`btn btn-sm ${item.ativo ? 'btn-outline-danger' : 'btn-outline-success'}`}
                  onClick={toggleAtivo} disabled={loading}>
                  {item.ativo ? 'Desativar' : 'Ativar'}
                </button>
              ) : <span />}
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancelar</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                  {loading ? <span className="spinner-border spinner-border-sm" /> : 'Salvar'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {modalNovaCor && (
        <ModalNovaCor onClose={() => setModalNovaCor(false)} onCriada={corRecemCriada} />
      )}
      {modalNovoTamanho && (
        <ModalNovoTamanho empresaAlvo={empresaAlvo} emGlobal={emGlobal}
          onClose={() => setModalNovoTamanho(false)} onCriado={tamanhoRecemCriado} />
      )}
    </div>
  );
}

export default function Modelos() {
  const { user } = useAuth();
  const souAdmin = user?.perfil === 'admin';
  const emGlobal = souAdmin && !user?.empresa_ativa_id;

  const [items, setItems] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [cores, setCores] = useState([]);
  const [tamanhos, setTamanhos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [idEmpresaFiltro, setIdEmpresaFiltro] = useState('');
  const [idClienteFiltro, setIdClienteFiltro] = useState('');
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [modal, setModal] = useState(null);
  const [modalFotos, setModalFotos] = useState(null);
  const [erroCarregar, setErroCarregar] = useState('');

  const carregar = useCallback(() => {
    setLoading(true);
    setErroCarregar('');
    Promise.all([api.listar(), tiposApi.listar(), coresApi.listar(), tamanhosApi.listar(), clientesApi.listar()])
      .then(([m, t, co, tam, cl]) => { setItems(m.data); setTipos(t.data); setCores(co.data); setTamanhos(tam.data); setClientes(cl.data); })
      .catch(() => setErroCarregar('Erro ao carregar dados. Recarregue a página.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (souAdmin) empresasApi.listEmpresas().then(r => setEmpresas(r.data)).catch(() => {});
  }, [souAdmin]);

  const tipoNome = id => tipos.find(t => t.id_tipo_uniforme === id)?.nome || '—';
  const empresaNome = id => empresas.find(e => e.id_empresa_operadora === id)?.nome || '—';
  const clienteNome = id => id ? (clientes.find(c => c.id_cliente_final === id)?.nome || '—') : 'Geral';
  const tamanhoNome = id => tamanhos.find(t => t.id_tamanho_uniforme === id)?.nome || id;

  const filtrados = items.filter(i => {
    const matchBusca = i.nome.toLowerCase().includes(busca.toLowerCase());
    const matchEmpresa = emGlobal && idEmpresaFiltro ? i.id_empresa_operadora === idEmpresaFiltro : true;
    const matchCliente = idClienteFiltro ? i.id_cliente_final === idClienteFiltro : true;
    return matchBusca && matchEmpresa && matchCliente;
  });

  const { sorted, Th } = useSortable(filtrados, 'nome');
  const { paginados, Paginacao } = usePaginacao(sorted);

  if (loading) return <LoadingSpinner />;

  const empresaFiltroOpts = [{ value: '', label: 'Todas as empresas' },
    ...empresas.map(e => ({ value: e.id_empresa_operadora, label: e.nome }))];
  const clienteFiltroOpts = [{ value: '', label: 'Todos os clientes' },
    ...clientes.map(c => ({ value: c.id_cliente_final, label: c.nome }))];

  return (
    <div>
      <PageHeader titulo="Modelos de Uniforme" subtitulo={`${items.length} modelos`}>
        <button className="btn btn-primary btn-sm" onClick={() => setModal({})}>+ Novo Modelo</button>
      </PageHeader>

      {erroCarregar && <div className="alert alert-danger py-2">{erroCarregar}</div>}

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <input className="form-control" style={{ maxWidth: 280 }}
          placeholder="Buscar por nome..." value={busca} onChange={e => setBusca(e.target.value)} />
        <select className="form-select" style={{ maxWidth: 220 }}
          value={idClienteFiltro} onChange={e => setIdClienteFiltro(e.target.value)}>
          {clienteFiltroOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {emGlobal && (
          <select className="form-select" style={{ maxWidth: 220 }}
            value={idEmpresaFiltro} onChange={e => setIdEmpresaFiltro(e.target.value)}>
            {empresaFiltroOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )}
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <Th campo="nome">Nome</Th>
                  <th>Cliente</th>
                  {emGlobal && !idEmpresaFiltro && <th>Empresa</th>}
                  <th>Tipo</th>
                  <th>Tamanhos</th>
                  <Th campo="preco_unitario">Preço</Th>
                  <Th campo="ativo">Status</Th>
                  <th style={{ width: 120 }}></th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 && (
                  <tr><td colSpan={emGlobal && !idEmpresaFiltro ? 8 : 7} className="text-center text-muted py-4">Nenhum modelo encontrado.</td></tr>
                )}
                {paginados.map(m => (
                  <tr key={m.id_modelo_uniforme}>
                    <td className="fw-semibold">{m.nome}</td>
                    <td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.id_cliente_final
                        ? clienteNome(m.id_cliente_final)
                        : <span className="badge bg-light text-dark border">Geral</span>}
                    </td>
                    {emGlobal && !idEmpresaFiltro && (
                      <td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {empresaNome(m.id_empresa_operadora)}
                      </td>
                    )}
                    <td>{tipoNome(m.id_tipo_uniforme)}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {(m.tamanhos || []).map(t => (
                          <span key={t.id_tamanho_uniforme} className="badge bg-secondary" style={{ fontSize: 10 }}>{tamanhoNome(t.id_tamanho_uniforme)}</span>
                        ))}
                      </div>
                    </td>
                    <td>
                      {m.tipo_preco === 'por_tamanho'
                        ? <span className="text-muted" style={{ fontSize: 12 }}>Por tamanho</span>
                        : formatMoeda(m.preco_unitario)}
                      {m.permite_variacao_cor && (
                        <span className="badge bg-info-subtle text-info-emphasis ms-1" style={{ fontSize: 10 }}>+cor</span>
                      )}
                    </td>
                    <td><StatusBadge status={m.ativo ? 'ativo' : 'inativo'} /></td>
                    <td>
                      <div className="d-flex gap-1">
                        <button className="btn btn-sm btn-outline-primary py-0 px-2"
                          onClick={() => setModal(m)}>Editar</button>
                        <button className="btn btn-sm btn-outline-secondary py-0 px-2"
                          onClick={() => setModalFotos(m)}>
                          Fotos {m.fotos?.length > 0 ? `(${m.fotos.length})` : ''}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Paginacao />

      {modal !== null && (
        <ModalModelo item={modal} tipos={tipos} cores={cores} tamanhos={tamanhos} clientes={clientes} empresas={empresas} emGlobal={emGlobal}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); carregar(); }}
          onCorCriada={(novaCor) => setCores(c => [...c, novaCor].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')))}
          onTamanhoCriado={(novoTamanho) => setTamanhos(t => [...t, novoTamanho])} />
      )}
      {modalFotos && (
        <ModalFotos modelo={modalFotos} onClose={() => setModalFotos(null)} onSaved={carregar} />
      )}
    </div>
  );
}
